import React from 'react';

const InvitationPreviewModal = ({ 
  isOpen, 
  onClose, 
  invitation, 
  templateId,
  inviteStyles,
  renderDecorativeElements,
  variant = 'modal' // 'modal' or 'inline'
}) => {
  if (!isOpen && variant === 'modal') return null;

  const template = inviteStyles.find(s => s.id === templateId);
  
  // Format date helper - avoid timezone issues by parsing date parts
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Variant-specific styles
  const styles = {
    modal: {
      container: 'p-12',
      border: 'border-4',
      rounded: 'rounded-2xl',
      names: 'text-4xl',
      text: 'text-lg py-4',
      divider: 'w-20 h-1',
      date: 'text-2xl',
      time: 'text-lg',
      venue: 'text-xl',
      address: 'text-base',
      rsvp: 'text-sm',
      additional: 'text-sm',
      footer: 'text-lg pt-6',
      spacing: 'space-y-4'
    },
    inline: {
      container: 'p-6 lg:p-8',
      border: 'border-4',
      rounded: 'rounded-xl lg:rounded-2xl',
      names: 'text-2xl lg:text-3xl',
      text: 'text-sm py-3',
      divider: 'w-12 lg:w-16 h-0.5',
      date: 'text-lg lg:text-xl',
      time: 'text-xs lg:text-sm',
      venue: 'text-base lg:text-lg',
      address: 'text-sm',
      rsvp: 'text-xs',
      additional: 'text-xs',
      footer: 'text-xs lg:text-sm pt-3 lg:pt-4',
      spacing: 'space-y-2 lg:space-y-3'
    }
  };

  const s = styles[variant];

  const previewContent = (
    <div 
      className={`relative ${s.rounded} ${s.container} ${s.border} shadow-xl transition-all duration-500`}
      style={{ 
        background: template?.background,
        borderColor: template?.accent,
        maxWidth: variant === 'modal' ? '600px' : undefined
      }}
    >
      {renderDecorativeElements(template?.decorative, template?.accent)}
      
      <div className={`relative z-10 text-center ${s.spacing} ${template?.font || 'font-serif'}`}>
        <div className={`${s.names} font-bold transition-colors duration-300`} style={{ color: template?.accent }}>
          {invitation.brideNames || invitation.bride_names || <span className="opacity-50">Bride's Name</span>} & {invitation.groomNames || invitation.groom_names || <span className="opacity-50">Groom's Name</span>}
        </div>
        
        {(invitation.text || invitation.invitation_text) && (
          <div className={`${s.text} text-gray-700 leading-relaxed`}>
            {invitation.text || invitation.invitation_text}
          </div>
        )}
        
        <div className={`flex items-center justify-center ${variant === 'inline' ? 'py-1 lg:py-2' : 'py-2'}`}>
          <div className={`${s.divider} rounded-full`} style={{ backgroundColor: template?.accent }} />
        </div>
        
        <div className={variant === 'inline' ? 'py-2 lg:py-3' : 'py-4'}>
          <div className={`${s.date} font-bold`} style={{ color: template?.accent }}>
            {formatDate(invitation.weddingDate || invitation.wedding_date) || 'Wedding Date'}
          </div>
          {(invitation.weddingTime || invitation.wedding_time) && (
            <div className={`${s.time} text-gray-700`}>{invitation.weddingTime || invitation.wedding_time}</div>
          )}
        </div>
        
        <div className={variant === 'inline' ? 'space-y-1' : 'space-y-2'}>
          <div className={`${s.venue} font-semibold`} style={{ color: template?.accent }}>
            {invitation.venue || 'Wedding Venue'}
          </div>
          {invitation.address && (
            <div className={`${s.address} text-gray-700`}>{invitation.address}</div>
          )}
        </div>
        
        {(invitation.rsvpDate || invitation.rsvp_date) && (
          <div className={variant === 'inline' ? 'pt-3' : 'pt-4'}>
            <div className={`${s.rsvp} font-semibold`} style={{ color: template?.accent }}>
              RSVP by {formatDate(invitation.rsvpDate || invitation.rsvp_date)}
            </div>
          </div>
        )}
        
        {(invitation.additionalInfo || invitation.additional_info) && (
          <div className={variant === 'inline' ? 'pt-3' : 'pt-4'}>
            <div className={`${s.additional} text-gray-600 italic`}>
              {invitation.additionalInfo || invitation.additional_info}
            </div>
          </div>
        )}
        
        <div className={`${s.footer} text-gray-700 italic`}>With love and joy</div>
      </div>
    </div>
  );

  if (variant === 'inline') {
    return previewContent;
  }

  // Modal wrapper
  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99999] flex items-center justify-center p-6"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto relative z-[99999]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-gradient-to-r from-rose-500 via-pink-500 to-gold-500 p-6 rounded-t-3xl flex items-center justify-between z-[100000]">
          <div className="flex items-center">
            <span className="text-3xl mr-3">💌</span>
            <h2 className="text-2xl font-serif font-bold text-white">Invitation Preview</h2>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-300 text-white text-xl font-bold hover:rotate-90"
          >
            &times;
          </button>
        </div>

        <div className="p-8">
          {previewContent}
        </div>
      </div>
    </div>
  );
};

export default InvitationPreviewModal;
